import type { Language, Skill } from "~/types/user";
import { useDebounceFn } from "@vueuse/core";

export function useSkillSlider() {
  const { $api } = useNuxtApp();
  const lastJson = reactive<Record<string, string>>({});

  const performUpdate = async (
    requestMethod: string,
    typeKey: string,
    payloadKey: string,
    endpoint: string,
    payload: any[],
    json: string
  ) => {
    try {
      await $api(endpoint, {
        method: requestMethod,
        body: {
          [payloadKey]: payload,
          only_append: false,
        },
      });
      lastJson[typeKey] = json;
    } catch (e) {
      console.error(`Error updating ${typeKey}:`, e);
    }
  };

  const createDebouncedUpdater = (
    requestMethod: string,
    typeKey: string,
    payloadKey: string,
    mapper: (item: any) => any,
    endpoint: string
  ) => {
    const debouncedFn = useDebounceFn(
      (data: any[]) => {
        const payload = (Array.isArray(data) ? data : []).map(mapper);
        const json = JSON.stringify(payload);

        if (json !== lastJson[typeKey]) {
          performUpdate(requestMethod, typeKey, payloadKey, endpoint, payload, json);
        }
      },
      500,
    );

    return (data: any[]) => {
      debouncedFn(data);
    };
  };

  const updateUserSkills = createDebouncedUpdater(
    "PUT",
    "skills",
    "skills",
    (s: Skill) => ({ id: s.id, experience: s.years_of_experience }),
    "/v2/user/skills"
  );

  const updateUserLanguages = createDebouncedUpdater(
    "POST",
    "languages",
    "user_languages",
    (l: Language) => ({ id: l.id, level_id: l.level_id, name: l.name }),
    "/v2/user/languages"
  );

  return {
    updateUserSkills,
    updateUserLanguages,
  };
}
